<script setup lang="ts">
import type { Component } from 'vue'
import { computed } from 'vue'

import type { PermNode } from '@yc/api-client'

import { ICONS } from './sideIcons'

const props = withDefaults(
  defineProps<{
    menus: readonly PermNode[]
    active?: string
    collapsed?: boolean
  }>(),
  { active: '', collapsed: false },
)

const emit = defineEmits<{
  (e: 'navigate', route: string): void
}>()

function iconOf(node: PermNode): string {
  return node.icon ?? ''
}

function iconComp(node: PermNode): Component | null {
  return ICONS[iconOf(node)] ?? null
}

// el-menu 在非 router 模式下 select 回传的 item 只有 {index, indexPath}，拿 item.route 永远是
// undefined（EP 只在 router 模式才把 route 放进 item）——菜单点了没反应就是这么来的。
// 挂载时 index 已经就是路由（清单里没配 route 的菜单挂的是权限码），按前缀区分即可。
function onSelect(index: string): void {
  if (index.startsWith('/')) emit('navigate', index)
}

const items = computed(() => props.menus)
</script>

<template>
  <el-menu
    class="yc-side"
    :default-active="props.active"
    :collapse="props.collapsed"
    :collapse-transition="false"
    unique-opened
    @select="onSelect"
  >
    <template v-for="node in items" :key="node.code">
      <el-sub-menu v-if="node.children?.length" :index="node.code">
        <template #title>
          <el-icon v-if="iconComp(node)" class="yc-side__icon">
            <component :is="iconComp(node)" />
          </el-icon>
          <span v-else-if="iconOf(node)" class="yc-side__icon">{{ iconOf(node) }}</span>
          <span>{{ node.name }}</span>
        </template>
        <el-menu-item v-for="child in node.children" :key="child.code" :index="child.route || child.code">
          {{ child.name }}
        </el-menu-item>
      </el-sub-menu>
      <el-menu-item v-else :index="node.route || node.code">
        <el-icon v-if="iconComp(node)" class="yc-side__icon">
          <component :is="iconComp(node)" />
        </el-icon>
        <span v-else-if="iconOf(node)" class="yc-side__icon">{{ iconOf(node) }}</span>
        <template #title>{{ node.name }}</template>
      </el-menu-item>
    </template>
  </el-menu>
</template>

<style scoped lang="scss">
.yc-side {
  border-right: 1px solid var(--yc-border);

  &__icon {
    margin-right: var(--yc-space-2);
    vertical-align: -0.125em;
  }

  :deep(.el-menu-item.is-active) {
    background: var(--yc-brand-subtle);
    font-weight: 500;
  }
}
</style>
